import type { AnyBackendMessageStages, BackendMessageStagesToValidated } from '../types';
import { createPlacementMessageBackend } from '../index';
import { defaultMessageBackend } from '../util/message-helpers';

export {
	createPlacement,
	createBackendPlacementRunners,
	createBackendPlacementMessageTemplateRouter,
	createPlacementMessageBackend,
} from '../index';

/**
 * Type-safe factory helpers for message templates installed into backend-only placements.
 *
 * Use this when there is no real PlacementBackend to import from a placement-specific package.
 * The actual placement (with the correct placement ID derived from the folder name) is created
 * at install time via the CLI — this object only provides the message-level factory methods.
 *
 * Methods:
 *   - createMessageBackend(stages): standard factory, pass your own backend stages
 *   - withDefaultMessageBackend(): shorthand for createMessageBackend(defaultMessageBackend)
 *
 * Usage in a message's index.tsx:
 *   import { DefaultHeadlessPlacementBackend } from '@post-office/placement-common-v2';
 *
 *   // Shorthand (recommended for simple backend-only messages):
 *   export const MessageBackend = DefaultHeadlessPlacementBackend.withDefaultMessageBackend();
 *
 *   // Or with custom stages:
 *   import { defaultMessageBackend } from '@post-office/placement-common-v2';
 *   export const MessageBackend = DefaultHeadlessPlacementBackend.createMessageBackend(defaultMessageBackend);
 */
// Backwards-compatible root export. Prefer importing from
// @post-office/placement-common-v2/frontend in product frontend code.
export { createHeadlessMessageFrontend } from '../../frontend/create-headless-message-frontend';

export const DefaultHeadlessPlacementBackend = {
	createMessageBackend: <T extends AnyBackendMessageStages>(
		stages: () => BackendMessageStagesToValidated<T>,
	): ReturnType<typeof createPlacementMessageBackend> => createPlacementMessageBackend(stages),
	withDefaultMessageBackend: (): ReturnType<typeof createPlacementMessageBackend> =>
		createPlacementMessageBackend(defaultMessageBackend),
} as const;
export { createPlacementStages } from '../util/runtime';
export { defaultMessageBackend, noValidation } from '../util/message-helpers';
export { useInitialData } from '@post-office/placement-contexts/hooks';
export type {
	AdditionalBackendStages,
	AdditionalFrontendStages,
	CustomPlacementDefinition,
	CustomDefaultFrontend,
	CustomBackend,
	CustomFrontend,
	CustomRenderer,
	CustomTransformMetadata,
	DefaultMessagesResponse,
	RML,
	FrontendStage,
	BackendPlacementStage,
	BackendMessageStage,
} from '../constraints/types';

export type { BackendMessageStageInput, PlacementRestMiddlewareConfig } from '../types';
export type { PlacementRouterMiddlewareFactory } from '../../placement-router/types';
export type {
	BackendMessageBody,
	BackendPlacementMessage,
	FrontendPlacementMessage,
	PlacementBackend,
	PlacementMessageBackendFactory,
	PlacementMessageFrontendFactory,
	PlacementFrontendFactory,
	PlacementBackendFactory,
	RequestContext,
} from '../types';
